import { useEffect, useCallback } from 'react'

interface KeyboardShortcut {
    key: string
    ctrl?: boolean
    shift?: boolean
    alt?: boolean
    action: () => void
    description: string
}

interface UseKeyboardShortcutsProps {
    shortcuts: KeyboardShortcut[]
    enabled?: boolean
}

export function useKeyboardShortcuts({ shortcuts, enabled = true }: UseKeyboardShortcutsProps) {
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (!enabled) return

        for (const shortcut of shortcuts) {
            const ctrlMatch = shortcut.ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey
            const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey
            const altMatch = shortcut.alt ? event.altKey : !event.altKey
            const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase()

            if (ctrlMatch && shiftMatch && altMatch && keyMatch) {
                event.preventDefault()
                shortcut.action()
                break
            }
        }
    }, [shortcuts, enabled])

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [handleKeyDown])
}

// Predefined shortcuts
export const SHORTCUTS = {
    SAVE: { key: 's', ctrl: true, description: 'Save file' },
    SEARCH_FILES: { key: 'p', ctrl: true, description: 'Search files' },
    NEXT_TAB: { key: 'Tab', ctrl: true, description: 'Next tab' },
    PREV_TAB: { key: 'Tab', ctrl: true, shift: true, description: 'Previous tab' },
    CLOSE_TAB: { key: 'w', ctrl: true, description: 'Close tab' },
    TOGGLE_PREVIEW: { key: 'e', ctrl: true, shift: true, description: 'Toggle preview' },
    ESCAPE: { key: 'Escape', description: 'Close modal/panel' }
}
